import Holidays from 'date-holidays';
import dayjs, { Dayjs } from 'dayjs';

const hd = new Holidays('SE', { languages: 'sv' });

/**
 * Days when Swedish workplaces are effectively closed.
 *
 * - `public` — the legal "röda dagar".
 * - `bank`   — Julafton, Midsommarafton, Nyårsafton. Not legally red days, but
 *   de-facto national closures. These are the days WD-4067's reporter actually
 *   got burned by, so omitting them defeats the purpose of the feature.
 *
 * Deliberately excluded:
 * - `observance` — mostly normal working days (Vasaloppet, Mors dag,
 *   Nobeldagen, Luciadagen). Including them would be noise.
 * - `optional` — the aftnar (Trettondagsafton, Valborgsmässoafton,
 *   Allhelgonaafton). Often half-days, but too uncertain to flag; product
 *   decision 2026-08-03 was to leave them unmarked.
 *
 * Keep this list in sync with backoffice-frontend's `src/utils/swedishHolidays.ts`.
 */
const CLOSED_TYPES = ['public', 'bank'] as const;

export type SwedishHolidayType = (typeof CLOSED_TYPES)[number];

export type SwedishHoliday = {
  name: string;
  /**
   * `public` = confirmed closure (legal red day).
   * `bank` = de-facto closure — render less emphatically, since an advisor
   * may legitimately still work.
   */
  type: SwedishHolidayType;
};

const cache: Record<number, Record<string, SwedishHoliday>> = {};

function getHolidayMap(year: number): Record<string, SwedishHoliday> {
  if (cache[year]) return cache[year];

  const map: Record<string, SwedishHoliday> = {};
  hd.getHolidays(year)
    .filter((h) => (CLOSED_TYPES as readonly string[]).includes(h.type))
    .forEach((h) => {
      // Use `h.date` (a wall-clock string like "2026-12-24 00:00:00") rather
      // than `h.start` (a Date already shifted into the runtime timezone).
      // Parsing the wall-clock string keeps the calendar date stable for
      // advisors outside Sweden.
      map[dayjs(h.date).format('YYYY-MM-DD')] = {
        name: h.name,
        type: h.type as SwedishHolidayType,
      };
    });

  cache[year] = map;
  return map;
}

export function getSwedishHoliday(date: Dayjs): SwedishHoliday | undefined {
  return getHolidayMap(date.year())[date.format('YYYY-MM-DD')];
}

export function getSwedishHolidayName(date: Dayjs): string | undefined {
  return getSwedishHoliday(date)?.name;
}
